import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Ship,
  Plane,
  Truck,
  Train,
  Warehouse,
  Send,
  AlertTriangle,
  Info,
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  Leaf,
  Loader,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { useTurnstile } from '../hooks/useTurnstile';
import { organizationSchema } from '../data/structuredData';

const API_BASE = process.env.REACT_APP_CARBON_API_URL || '';

// Public site key — safe in the bundle, unlike the secret, which stays a stack
// parameter on the backend. Unset disables the challenge here, matching a blank
// TurnstileSecretKey disabling it in the handler, so the two halves can be
// switched on independently without either breaking the other.
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

// Shown when the service itself is saturated, as opposed to when this visitor
// has personally sent too many requests — those are different conditions and the
// second one is not their fault.
//
// Deliberately does NOT claim "lots of people are using this". The concurrency
// cap is small, so a capacity rejection is as likely to be one automated client
// as real demand; asserting a cause we cannot observe reads as an excuse when it
// turns out to be wrong. State the condition, explain the constraint, give a
// concrete wait.
const CAPACITY_MESSAGE =
  'The calculator is at capacity right now. It runs on a deliberately small ' +
  'footprint to keep it free, so only a few conversations can run at once. ' +
  'Please try again in a minute.';

const EXAMPLES = [
  { label: '2 × 40HC ocean import', text: '2 x 40HC from Shanghai to Nashville, about 18 tonnes each' },
  { label: 'Air freight', text: '500 kg of electronics air freight from Los Angeles to Frankfurt' },
  { label: 'Reefer', text: 'One 40ft reefer of produce from Rotterdam to New York, 22 days door to door' },
  { label: 'Ocean vs air', text: 'Compare ocean and air freight for 3 tonnes from Shenzhen to Chicago' },
];

const MODE_ICON = {
  ocean: Ship,
  air: Plane,
  road: Truck,
  rail: Train,
  inland_waterway: Ship,
};

const TIER_STYLE = {
  default: {
    label: 'Screening estimate',
    className: 'bg-amber-50 text-amber-900 border-amber-200',
    blurb: 'Default emission factors and estimated distances.',
  },
  modelled: {
    label: 'Modelled data',
    className: 'bg-blue-50 text-blue-900 border-blue-200',
    blurb: 'Routed distances with operation-specific factors.',
  },
  primary: {
    label: 'Primary data',
    className: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    blurb: 'Based on measured fuel or energy consumption.',
  },
};

/** Formats a number of kg CO2e into a readable figure with a sensible unit. */
function formatMass(kg) {
  if (kg == null) return '—';
  if (kg >= 1000) return `${(kg / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })} t`;
  return `${kg.toLocaleString(undefined, { maximumFractionDigits: 1 })} kg`;
}

/** Short mode summary for a calculation, used to label comparison options. */
const MODE_LABEL = {
  ocean: 'Ocean',
  air: 'Air',
  road: 'Road',
  rail: 'Rail',
  inland_waterway: 'Barge',
};

/**
 * Fallback name for one option in a comparison, derived from the modes it uses.
 *
 * Only reached when the model did not supply a `scenarioLabel`. Its own label is
 * better whenever it exists — "Ocean via Los Angeles" says something this cannot,
 * and two options can easily share a mode mix ("with and without the barge leg")
 * and so produce the same derived name.
 */
function chainLabel(result) {
  const modes = [
    ...new Set((result.chain || []).filter((l) => l.kind === 'toc').map((l) => l.mode)),
  ];
  return modes.map((m) => MODE_LABEL[m] || m).join(' + ') || 'Option';
}

function formatNumber(n, dp = 0) {
  if (n == null) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: dp });
}

/**
 * Minimal inline renderer for **bold** spans.
 * The model occasionally emphasises the headline figure; pulling in a full
 * markdown library for one pattern would not earn its bundle size.
 */
function renderInline(text) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-semibold text-black">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function MessageBubble({ role, content }) {
  const isUser = role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] px-4 py-3 rounded-lg text-sm leading-relaxed ${
          isUser ? 'bg-black text-white' : 'bg-gray-100 text-gray-900'
        }`}
      >
        {String(content)
          .split('\n')
          .filter((line) => line.trim() !== '')
          .map((line, i) => (
            <p key={i} className={i > 0 ? 'mt-3' : ''}>
              {renderInline(line)}
            </p>
          ))}
      </div>
    </div>
  );
}

function DataQualityBadge({ tier }) {
  const style = TIER_STYLE[tier] || TIER_STYLE.default;
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1 border rounded-full text-xs font-medium ${style.className}`}>
      <Info className="w-3.5 h-3.5" />
      {style.label}
    </div>
  );
}

function ResultCard({ result }) {
  const { totals, intensity, dataQuality, aviationNonCo2, meta } = result;
  const tierStyle = TIER_STYLE[dataQuality.tier] || TIER_STYLE.default;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-black text-white p-6">
        <div className="text-xs uppercase tracking-wider text-gray-400 mb-2">
          Total emissions, well-to-wheel
        </div>
        <div className="text-4xl font-bold mb-1">{formatMass(totals.wtwKgCo2e)}</div>
        <div className="text-sm text-gray-400">CO<sub>2</sub>e</div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6 pt-6 border-t border-white/15 text-sm">
          <div>
            <div className="text-gray-400 text-xs mb-1">Tank-to-wheel</div>
            <div className="font-semibold">{formatMass(totals.ttwKgCo2e)}</div>
          </div>
          <div>
            <div className="text-gray-400 text-xs mb-1">Intensity</div>
            <div className="font-semibold">
              {intensity.gCo2ePerTonneKm != null
                ? `${formatNumber(intensity.gCo2ePerTonneKm, 1)} g/t-km`
                : '—'}
            </div>
          </div>
          <div>
            <div className="text-gray-400 text-xs mb-1">Transport</div>
            <div className="font-semibold">{formatMass(totals.transportKgCo2e)}</div>
          </div>
          <div>
            <div className="text-gray-400 text-xs mb-1">Hubs &amp; handling</div>
            <div className="font-semibold">{formatMass(totals.hubKgCo2e)}</div>
          </div>
          {totals.refrigerantKgCo2e > 0 && (
            <div className="col-span-2 lg:col-span-4">
              <div className="text-gray-400 text-xs mb-1">Refrigerant leakage</div>
              <div className="font-semibold">{formatMass(totals.refrigerantKgCo2e)}</div>
            </div>
          )}
        </div>
      </div>

      <div className="p-4 bg-gray-50 border-t border-gray-200">
        <div className="flex items-start gap-3">
          <DataQualityBadge tier={dataQuality.tier} />
          <p className="text-xs text-gray-600 leading-relaxed flex-1">{tierStyle.blurb}</p>
        </div>
        {/* Provenance travels with the number — a screenshot of this card should
            never leave the reader thinking these are licensed GLEC figures. */}
        {meta?.factorProvenance && (
          <p className="text-xs text-gray-500 leading-relaxed mt-3 pt-3 border-t border-gray-200">
            {meta.factorProvenance}
          </p>
        )}
      </div>

      {aviationNonCo2 && (
        <div className="p-4 bg-orange-50 border-t border-orange-200">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-orange-900 leading-relaxed">
              <span className="font-semibold">
                {`Plus ${formatMass(aviationNonCo2.kgCo2e)} from aviation non-CO`}<sub>2</sub> effects
              </span> (contrails and NO<sub>x</sub>
              {`), which roughly ${aviationNonCo2.multiplier}× the warming impact of the flight. ISO 14083 excludes this from the headline figure, so it is shown separately rather than added in.`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ChainBreakdown({ chain }) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-black">Transport chain</h3>
      </div>
      <div className="divide-y divide-gray-100">
        {chain.map((leg) => {
          const Icon = leg.kind === 'hoc' ? Warehouse : MODE_ICON[leg.mode] || Truck;
          return (
            <div key={leg.position} className="p-4 flex items-start gap-3">
              <div className="w-8 h-8 bg-gray-100 flex items-center justify-center rounded flex-shrink-0">
                <Icon className="w-4 h-4 text-gray-700" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-3">
                  <div className="text-sm font-medium text-black truncate">{leg.label}</div>
                  <div className="text-sm font-semibold text-black whitespace-nowrap">
                    {formatMass(leg.wtwKgCo2e)}
                  </div>
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {leg.kind === 'toc'
                    ? `${leg.from} → ${leg.to} · ${formatNumber(leg.distanceKm)} km${
                        leg.routing ? ` · via ${leg.routing}` : ''
                      }`
                    : `${leg.location} · ${formatNumber(leg.units, 2)} ${
                        leg.throughputBasis === 'per_teu' ? 'TEU' : 'tonnes'
                      } handled`}
                </div>
                {leg.kind === 'toc' && leg.massBasis === 'volumetric_mass' && (
                  <div className="text-xs text-amber-700 mt-1">
                    {`Volume-limited — charged on ${formatNumber(
                      leg.chargeableMassKg
                    )} kg volumetric mass`}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The span of the whole answer, as opposed to the per-assumption swings in the
 * ledger below it.
 *
 * The ledger answers "which lever is worth pulling". Someone deciding on the
 * basis of this number needs the other question answered too — how wrong could
 * it be — and the top swing understates that whenever several assumptions point
 * the same way.
 *
 * Presented carefully, because the failure mode here is a range that LOOKS
 * statistical. The bounds are plausible ranges rather than distributions, and
 * both ends assume every assumption is wrong in the same direction at once. So:
 * no "±", which would imply both symmetry and a margin of error, and the
 * asymmetry is shown rather than averaged away — an estimate sitting left of
 * centre is one far likelier to be under than over.
 */
function UncertaintyRange({ uncertainty }) {
  if (!uncertainty) return null;

  const { baseKgCo2e, lowKgCo2e, highKgCo2e, lowPctOfBase, highPctOfBase, assumptionsVaried } =
    uncertainty;

  const span = highKgCo2e - lowKgCo2e;
  const rawPosition = span > 0 ? ((baseKgCo2e - lowKgCo2e) / span) * 100 : 50;
  const position = Math.min(100, Math.max(0, rawPosition));

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-black">How wrong could this be?</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          {`Where the total lands if every one of the ${assumptionsVaried} assumptions below is wrong in the same direction at once.`}
        </p>
      </div>

      <div className="p-4">
        {/* Top-aligned, not bottom: the middle column has no percentage line,
            so aligning at the bottom dropped the estimate a row below the two
            figures it is meant to sit between. */}
        <div className="grid grid-cols-3 gap-2 items-start">
          <div>
            <div className="text-xs text-gray-500 mb-1">All optimistic</div>
            <div className="text-sm font-semibold text-gray-900">{formatMass(lowKgCo2e)}</div>
            <div className="text-xs text-emerald-700">{`${lowPctOfBase}%`}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500 mb-1">Estimate</div>
            <div className="text-sm font-bold text-black">{formatMass(baseKgCo2e)}</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-500 mb-1">All pessimistic</div>
            <div className="text-sm font-semibold text-gray-900">{formatMass(highKgCo2e)}</div>
            <div className="text-xs text-amber-700">{`+${highPctOfBase}%`}</div>
          </div>
        </div>

        <div className="relative mt-3 mb-1">
          <div className="h-2 rounded-full bg-gradient-to-r from-emerald-200 to-amber-300" />
          {/* Marker only — the numbers live in the grid above, so nothing has to
              be absolutely positioned near the panel edges. */}
          <div
            className="absolute top-0 h-2 w-0.5 bg-black"
            style={{ left: `${position}%` }}
            aria-hidden="true"
          />
        </div>

        <p className="text-xs text-gray-600 leading-relaxed mt-3">
          This is a range of scenarios, not a margin of error — the bounds are plausible values
          rather than statistical limits, and reality normally sits well inside them. Correcting
          the first assumption below narrows it fastest.
        </p>
      </div>
    </div>
  );
}

function AssumptionLedger({ assumptions, sensitivity }) {
  if (!assumptions || assumptions.length === 0) return null;
  const topSwing = sensitivity && sensitivity.length > 0 ? sensitivity[0] : null;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-black">What we assumed</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Anything you did not state was filled from sourced defaults. Tell the assistant the real
          value to tighten the estimate.
        </p>
      </div>

      {topSwing && (
        <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-start gap-2">
          <TrendingUp className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900 leading-relaxed">
            <span className="font-semibold">{`Correct this one first: ${topSwing.label}.`}</span>
            {` Across its plausible range the total moves by ${formatMass(
              topSwing.swingKgCo2e
            )} — about ${topSwing.swingPctOfBase}% of the headline figure.`}
          </div>
        </div>
      )}

      <div className="divide-y divide-gray-100">
        {assumptions.map((a) => (
          <div key={a.id} className="p-4">
            <div className="flex items-baseline justify-between gap-3 mb-1">
              <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                {a.label}
              </div>
              <div className="text-sm text-gray-700 whitespace-nowrap">
                {`${typeof a.value === 'number' ? formatNumber(a.value, 2) : String(a.value)}${
                  a.unit ? ` ${a.unit}` : ''
                }`}
              </div>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">{a.rationale}</p>
            <p className="text-xs text-gray-400 mt-1">{`Source: ${a.source}`}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ModellingNotes({ notes }) {
  if (!notes || notes.length === 0) return null;
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-black">Routing decisions</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Choices the assistant made that you did not specify. Push back on any of them.
        </p>
      </div>
      <ul className="p-4 space-y-2">
        {notes.map((note, i) => (
          <li key={i} className="text-xs text-gray-700 flex items-start gap-2 leading-relaxed">
            <span className="mt-1.5 w-1 h-1 bg-gray-400 rounded-full flex-shrink-0" />
            {note}
          </li>
        ))}
      </ul>
    </div>
  );
}

function EmptySheet() {
  return (
    <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center">
      <Leaf className="w-8 h-8 text-gray-300 mx-auto mb-3" />
      <h3 className="text-sm font-semibold text-gray-700 mb-2">Your shipment sheet</h3>
      <p className="text-xs text-gray-500 leading-relaxed mb-4">
        Describe a freight movement and the breakdown, assumptions, and sensitivity analysis appear
        here.
      </p>
      <div className="text-left bg-gray-50 rounded p-4">
        <div className="text-xs font-semibold text-gray-700 mb-2">Three things are required</div>
        <ul className="space-y-1.5">
          {['An origin', 'A destination', 'A weight, volume, or container count'].map((item) => (
            <li key={item} className="text-xs text-gray-600 flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
        <p className="text-xs text-gray-500 mt-3 leading-relaxed">
          Everything else — vessel class, load factor, transit time, hub handling — is defaulted from
          the factor library and disclosed.
        </p>
      </div>
    </div>
  );
}

const CarbonCalculatorPage = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  // A list, because a comparison request ("ocean vs air", "half by barge")
  // produces several calculations. Keeping only one is what allowed the result
  // panel to show a different figure from the one the chat led with.
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);
  // Only set when the visitor hit their OWN rate limit — see the note at the
  // throw site for why capacity failures deliberately do not trigger this.
  const [showCta, setShowCta] = useState(false);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  // Stays empty until a visitor actually sends something — nothing renders into
  // it during the react-snap snapshot. See useTurnstile for why that matters.
  const turnstileRef = useRef(null);
  const getTurnstileToken = useTurnstile(TURNSTILE_SITE_KEY, turnstileRef);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = useCallback(
    async (text) => {
      const trimmed = text.trim();
      if (!trimmed || loading) return;

      if (!API_BASE) {
        setError(
          'The calculator API is not configured for this build. Set REACT_APP_CARBON_API_URL to the deployed endpoint and rebuild.'
        );
        return;
      }

      const nextMessages = [...messages, { role: 'user', content: trimmed }];
      setMessages(nextMessages);
      setInput('');
      setLoading(true);
      setError(null);
      setShowCta(false);

      try {
        // Minted per request: Turnstile tokens are single-use, so reusing one
        // across a conversation would authorise the first message and fail
        // every one after it. Null when the challenge is unconfigured or
        // unreachable — the handler decides what that means, since it is the
        // only side that knows whether verification is switched on.
        const turnstileToken = await getTurnstileToken();

        const response = await fetch(`${API_BASE}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messages: nextMessages,
            ...(turnstileToken ? { turnstileToken } : {}),
          }),
        });

        // Check status BEFORE parsing. Rate-limit and capacity rejections are
        // generated by API Gateway, not by our handler, so they do not carry the
        // { error } shape — parsing first turns a clear 429 into a JSON syntax
        // error and loses the reason entirely.
        if (!response.ok) {
          let body = null;
          try {
            body = await response.json();
          } catch {
            // API Gateway rejections are not JSON in our shape.
          }

          // Our handler's 429 carries { error } and means THIS visitor sent too
          // many requests. A 429 with no such body came from API Gateway, which
          // means the service as a whole is saturated — a different situation,
          // and not something the visitor did.
          let message;
          if (body && body.error) {
            message = body.error;
          } else if (response.status === 429 || response.status === 503) {
            message = CAPACITY_MESSAGE;
          } else {
            message = 'The calculation service returned an error. Please try again.';
          }

          // Someone who has run enough calculations to hit their own limit is
          // demonstrating real intent, so this is the one failure worth turning
          // into a conversation. Capacity rejections are NOT — the visitor may
          // have done nothing at all, and pitching them reads as opportunistic.
          const err = new Error(message);
          // Both of these are worth a conversation, for different reasons: the
          // rate limit means this visitor is working the tool hard, and the daily
          // budget means the free tier genuinely cannot serve them today. Plain
          // capacity rejections still get no CTA — that visitor may have done
          // nothing but click once.
          if (body && (body.reason === 'rate_limited' || body.reason === 'daily_budget_exhausted')) {
            err.showCta = true;
          }
          throw err;
        }

        const data = await response.json();

        setMessages([...nextMessages, { role: 'assistant', content: data.reply }]);
        if (Array.isArray(data.results) && data.results.length > 0) {
          setResults(data.results);
        } else if (data.result) {
          setResults([data.result]);
        }
      } catch (err) {
        // A failed fetch (network drop, blocked by CORS, capacity rejection with
        // no CORS headers) surfaces as a TypeError whose message is the useless
        // "Failed to fetch". Anything we threw above is already user-facing.
        // A capacity rejection generated by the gateway may be blocked by the
        // browser before we can read its status, landing here instead. We cannot
        // tell that apart from a dropped connection, so the copy covers both
        // rather than confidently naming the wrong cause.
        const isNetworkFailure = err instanceof TypeError;
        setError(
          isNetworkFailure
            ? 'Could not reach the calculation service — it may be busy, or your connection may have dropped. Please try again in a moment.'
            : err.message || 'Something went wrong. Please try again.'
        );
        setShowCta(Boolean(err.showCta));
      } finally {
        setLoading(false);
        if (inputRef.current) inputRef.current.focus();
      }
    },
    [messages, loading, getTurnstileToken]
  );

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Freight Carbon Calculator',
    url: 'https://tarelabs.com/tools/freight-carbon-calculator',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description:
      'Estimate freight shipment emissions using the ISO 14083 and GLEC Framework methodology. ' +
      'Describe a shipment in plain language and get a CO2e breakdown by leg, with every ' +
      'assumption disclosed.',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    provider: { '@id': 'https://tarelabs.com/#organization' },
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Freight Carbon Calculator — ISO 14083 Shipment Emissions"
        description="Estimate freight emissions for any shipment. Describe the move in plain language and get an ISO 14083 / GLEC-structured CO2e breakdown by leg, with every assumption and its sensitivity disclosed."
        path="/tools/freight-carbon-calculator"
        jsonLd={[pageSchema, organizationSchema]}
      />
      <Header />

      {/* Hero */}
      <section className="pt-28 pb-10 px-6 border-b border-gray-200">
        {/* Two columns on desktop: the caveat moves out of the vertical stack
            and into the empty right half, which both fills it and shortens the
            hero enough to keep the calculator above the fold on a laptop. */}
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full text-xs font-medium text-gray-700 mb-4">
              <Leaf className="w-3.5 h-3.5" />
              Follows the ISO 14083 · GLEC v3 methodology
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-black mb-4">
              Freight Carbon Calculator
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl leading-relaxed">
              Describe a shipment the way you would to a colleague. You get a CO<sub>2</sub>e
              breakdown by leg, every assumption spelled out, and an honest read on which of those
              assumptions actually moves the number.
            </p>
          </div>
          {/* Rule turns from horizontal (stacked) to vertical (side by side). */}
          {/* Column stays stretched so the rule spans the full hero height;
              the text is centred within it rather than by the grid. */}
          <aside className="lg:col-span-5 mt-4 pt-4 border-t border-gray-200 lg:mt-0 lg:pt-0 lg:border-t-0 lg:border-l lg:pl-8 lg:flex lg:flex-col lg:justify-center">
            <p className="text-sm text-gray-500 max-w-2xl leading-relaxed">
              The <strong className="font-semibold text-gray-700">methodology</strong> follows ISO
              14083 and the GLEC Framework.
              The <strong className="font-semibold text-gray-700">emission factors</strong> are
              representative defaults we compiled — not licensed GLEC factor-library data. That
              makes this a screening tool: right for comparing options and finding hotspots, not for
              an assured Scope 3 submission.
            </p>
          </aside>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-10 px-6">
        {/* items-start keeps each column at its natural height — a stretched
            column would fill the row and leave the sticky rail nothing to move
            within. The 5/7 split follows content density rather than symmetry:
            the sheet carries far more text than the composer. */}
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Chat — sticky rail on desktop, normal flow on mobile */}
          <div className="lg:col-span-5 flex flex-col lg:sticky lg:top-24 lg:h-[min(calc(100vh-8rem),640px)]">
            <div
              ref={scrollRef}
              className="flex-1 min-h-[340px] max-h-[560px] lg:min-h-0 lg:max-h-none overflow-y-auto border border-gray-200 rounded-lg p-4 space-y-4 bg-white"
            >
              {messages.length === 0 && (
                <div className="h-full flex flex-col justify-center">
                  <p className="text-sm text-gray-500 mb-4 text-center">
                    Try one of these, or describe your own shipment.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {EXAMPLES.map((ex) => (
                      <button
                        key={ex.label}
                        onClick={() => send(ex.text)}
                        className="px-3 py-2 text-xs font-medium border border-gray-300 rounded-full hover:border-black hover:bg-black hover:text-white transition-colors"
                      >
                        {ex.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, i) => (
                <MessageBubble key={i} role={m.role} content={m.content} />
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 px-4 py-3 rounded-lg flex items-center gap-2 text-sm text-gray-600">
                    <Loader className="w-4 h-4 animate-spin" />
                    Working through the chain…
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-800 leading-relaxed">{error}</p>
              </div>
            )}

            {/* Shown only when the visitor hit their OWN request limit. Running
                this many calculations is a strong signal of real intent, so the
                limit is worth reframing as an invitation rather than a wall.
                Capacity failures never reach here — see the throw site. */}
            {showCta && (
              <div className="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-sm font-semibold text-black mb-1">
                  Looks like you&apos;re putting this to real work.
                </p>
                <p className="text-xs text-gray-600 leading-relaxed">
                  The free calculator is rate-limited because it runs on a small,
                  deliberately cheap footprint. If you need this across a real lane
                  network — your own carrier data, your factors, no limits — that is
                  the sort of thing we build.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a
                    href="https://calendly.com/dan-tarelabs/new-meeting"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-black text-white text-xs font-medium rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    Book a free consultation
                    <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                  <a
                    href="mailto:dan@tarelabs.com?subject=Freight%20carbon%20calculator"
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-xs font-medium rounded-lg hover:border-black transition-colors"
                  >
                    Email us
                  </a>
                </div>
              </div>
            )}

            <div className="mt-3 flex gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={2}
                placeholder="e.g. 3 pallets, 1,200 kg, Rotterdam to Chicago by ocean and truck"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:border-black transition-colors"
                disabled={loading}
              />
              <button
                onClick={() => send(input)}
                disabled={loading || !input.trim()}
                className="px-5 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                aria-label="Send message"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            {/* Turnstile renders here, and only for a visitor Cloudflare
                decides to challenge. Empty and zero-height otherwise —
                including in the prerendered snapshot, which is why it is a
                bare container React never puts children into. */}
            <div ref={turnstileRef} className="mt-2 empty:hidden" />
            <p className="text-xs text-gray-400 mt-2">
              Enter to send · Shift+Enter for a new line
            </p>
          </div>

          {/* Shipment sheet — single reading column: the assumption ledger is
              rank-ordered, so splitting it across columns would break the rank. */}
          <div className="lg:col-span-7 space-y-5">
            {results.length === 0 && <EmptySheet />}
            {results.length > 0 && (
              <>
                {/* One full block per calculation. When the model modelled a
                    comparison it produced several, and rendering only one is
                    exactly how this panel came to disagree with the chat. */}
                {results.map((r, idx) => (
                  <React.Fragment key={idx}>
                    {results.length > 1 && (
                      <div className="flex items-baseline justify-between pt-2 border-t-2 border-black">
                        <h3 className="text-sm font-bold uppercase tracking-wider">
                          {`Option ${idx + 1} · ${r.scenarioLabel || chainLabel(r)}`}
                        </h3>
                        <span className="text-sm font-semibold">
                          {formatMass(r.totals.wtwKgCo2e)}
                        </span>
                      </div>
                    )}
                    <ResultCard result={r} />
                    {/* Directly under the headline figure: the number and how
                        much it could move belong together, before the detail. */}
                    <UncertaintyRange uncertainty={r.uncertainty} />
                    <ChainBreakdown chain={r.chain} />
                    <AssumptionLedger
                      assumptions={r.assumptions}
                      sensitivity={r.sensitivity}
                    />
                    <ModellingNotes notes={r.modellingNotes} />

                    {r.dataQuality.warnings && r.dataQuality.warnings.length > 0 && (
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
                        {r.dataQuality.warnings.map((w, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-900 leading-relaxed">{w}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </React.Fragment>
                ))}

                {/* Lead capture */}
                <div className="bg-black text-white rounded-lg p-6">
                  <h3 className="font-bold mb-2">Need this on your real freight data?</h3>
                  <p className="text-sm text-gray-300 leading-relaxed mb-4">
                    This runs on default factors. We build the version that runs on your carrier
                    data — wired into your TMS or ERP, with primary fuel data where your carriers
                    report it, and output your auditors will accept.
                  </p>
                  <Link
                    to="/#contact"
                    className="inline-flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded font-medium text-sm hover:bg-gray-100 transition-colors"
                  >
                    Talk to us
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Methodology — static, indexable content */}
      <section className="py-16 px-6 bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">How this is calculated</h2>
          <p className="text-gray-600 leading-relaxed mb-10">
            Freight emissions accounting is governed by <strong>ISO 14083:2023</strong>, with
            the <strong>GLEC Framework v3</strong> as its practical implementation. Above both sits the
            GHG Protocol, where freight lands in Scope 3 Category 4 and Category 9. This tool follows
            that structure rather than the mass × distance shortcut most free calculators use.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                title: 'Chains, not single legs',
                body: 'A movement decomposes into transport legs (TOCs) and hub events (HOCs) at ports, terminals, and warehouses. Hub emissions are real and routinely ignored — a chilled warehouse can rival a road leg.',
              },
              {
                title: 'Well-to-wheel, not just combustion',
                body: 'Emissions include upstream fuel production, not only what comes out of the tailpipe. That matters most for electric vehicles, where tank-to-wheel is zero but well-to-wheel plainly is not.',
              },
              {
                title: 'Distance has a hierarchy',
                body: 'Measured distance beats routed network distance, which beats great-circle plus a correction. Ocean legs use routed sea distances that account for Suez, Panama, and the Cape — great-circle understates Asia–Europe by roughly half.',
              },
              {
                title: 'Allocation reflects the real constraint',
                body: 'Light, bulky freight consumes vehicle capacity by volume, not weight. Allocating on gross weight understates it, so chargeable mass uses the constraining basis — the same logic your carrier bills on.',
              },
              {
                title: 'Refrigeration is two effects',
                body: 'Reefer cargo adds energy to run the unit and refrigerant leakage from F-gases with global warming potentials in the thousands. The second one is the one that usually gets missed.',
              },
              {
                title: 'Aviation non-CO₂ is reported separately',
                body: 'Contrails and NOₓ roughly double air freight’s warming impact. ISO 14083 excludes them from the headline number, so we show them as their own line rather than quietly folding them in.',
              },
            ].map((item) => (
              <div key={item.title}>
                <h3 className="font-bold text-black mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 p-6 bg-white border border-gray-200 rounded-lg">
            <h3 className="font-bold text-black mb-2">Where these numbers come from</h3>
            <p className="text-sm text-gray-600 leading-relaxed mb-4">
              Two separate things get conflated in carbon tooling, so it is worth being precise
              about which one this is. The <strong className="text-black">methodology</strong> —
              how a chain is decomposed, what counts as an emission, how distance and mass are
              resolved — follows ISO 14083 and the GLEC Framework.
              The <strong className="text-black">emission factors</strong> are our own compilation of
              representative industry values. They are not the licensed GLEC factor library, and
              we are not affiliated with the Smart Freight Centre.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed mb-4">
              Those factors are the right order of magnitude and internally consistent, which makes
              this genuinely useful for comparing modal options, sizing a lane, or finding the
              hotspots in a network. It is not what you attach to an assured Scope 3 submission.
              Every result carries a data-quality tier for exactly that reason — the honest answer
              depends entirely on what went into it.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Closing that gap means licensed factors or, better, primary fuel data reported by
              your carriers and wired into your TMS. The calculation engine here is built so that
              swapping the factor library is a data change rather than a rebuild — which is the
              work we actually do for clients.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CarbonCalculatorPage;
